import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import api, { clearCsrfToken } from '../../services/api';

import {
  completeInvitedPasswordSetup as completeInvitedPasswordSetupRequest,
  resendSignupVerification,
  verifySignupEmail,
} from '../../services/authService';

import {
  needsEmailVerification,
  needsPasswordChange,
  normalizeAuthResponse,
} from '../../utils/authVerification';
import { extractSubscriptionFromUserPayload } from '../../services/subscriptionService';
import {
  createAdditionalOrganization as createAdditionalOrganizationRequest,
  switchOrganizationPrimary as switchOrganizationPrimaryRequest,
} from '../../services/organizationService';
import { mergeUserFromAuthPayload, mergeUserWithOrganizations } from '../../utils/organizations';
import { setActiveOrganizationId } from '../../utils/recipientContactsStore';



const getStoredToken = () => {

  try {

    return localStorage.getItem('auth_token');

  } catch (error) {

    return null;

  }

};



const getStoredUser = () => {

  try {

    const userStr = localStorage.getItem('auth_user');

    return userStr ? JSON.parse(userStr) : null;

  } catch (error) {

    return null;

  }

};



const storedUser = getStoredUser();



const initialState = {

  user: storedUser,

  token: getStoredToken(),

  verificationRequired:
    localStorage.getItem('auth_verification_required') === 'true' ||
    (storedUser ? needsEmailVerification(storedUser) : false),
  mustChangePassword:
    localStorage.getItem('auth_must_change_password') === 'true' ||
    (storedUser ? needsPasswordChange(storedUser) : false),

  status: 'idle',

  error: null,

};



const persistAuth = ({ token, user, verificationRequired, mustChangePassword }) => {

  if (token) {

    localStorage.setItem('auth_token', token);

  }

  if (user) {

    localStorage.setItem('auth_user', JSON.stringify(user));

  }

  if (verificationRequired !== undefined) {

    localStorage.setItem(

      'auth_verification_required',

      verificationRequired ? 'true' : 'false'

    );

  }

  if (mustChangePassword !== undefined) {
    localStorage.setItem(
      'auth_must_change_password',
      mustChangePassword ? 'true' : 'false'
    );
  }

};



const clearStoredAuth = () => {

  localStorage.removeItem('auth_token');

  localStorage.removeItem('auth_user');

  localStorage.removeItem('auth_verification_required');

  localStorage.removeItem('auth_pending_email');
  localStorage.removeItem('auth_must_change_password');

};

/** Login/verify payloads omit memberships; GET /user includes `organizations`. */
const enrichAuthFromProfile = async (auth) => {
  if (!auth?.token || auth.verificationRequired || auth.mustChangePassword) {
    return auth;
  }

  try {
    const response = await api.get('/user');
    const data = response.data;
    const user = mergeUserFromAuthPayload(data) || data.user || auth.user;
    if (!user) return auth;

    const verificationRequired =
      data.user?.verification_required ?? auth.verificationRequired ?? needsEmailVerification(user);
    const mustChangePassword =
      data.user?.must_change_password ?? auth.mustChangePassword ?? needsPasswordChange(user);
    const subscription =
      extractSubscriptionFromUserPayload(data) ?? auth.subscription;

    persistAuth({
      token: auth.token,
      user,
      verificationRequired,
      mustChangePassword,
    });

    if (
      user.active_organization_id != null
      || user.organization_id != null
      || user.primary_organization_id != null
    ) {
      setActiveOrganizationId(
        user.active_organization_id ?? user.organization_id ?? user.primary_organization_id
      );
    }

    return {
      ...auth,
      user,
      verificationRequired,
      mustChangePassword,
      subscription,
    };
  } catch {
    return auth;
  }
};

export const login = createAsyncThunk(

  'auth/login',

  async (credentials, { rejectWithValue }) => {

    try {

      const response = await api.post('/login', credentials);

      const data = response.data;

      const auth = normalizeAuthResponse(data, { fallbackEmail: credentials.email });

      if (!auth.token) {

        return rejectWithValue('Invalid response from server. Token not found.');

      }

      if (auth.user?.email || credentials.email) {
        localStorage.setItem(
          'auth_pending_email',
          auth.user?.email || credentials.email
        );
      }

      persistAuth({

        token: auth.token,

        user: auth.user,

        verificationRequired: auth.verificationRequired,
        mustChangePassword: auth.mustChangePassword,

      });

      return enrichAuthFromProfile(auth);

    } catch (error) {

      const errorData = error.response?.data;

      let errorMessage = 'Login failed. Please check your credentials.';



      if (errorData?.errors) {

        const firstKey = Object.keys(errorData.errors)[0];

        errorMessage = errorData.errors[firstKey][0] || errorMessage;

      } else if (errorData?.message) {

        errorMessage = errorData.message;

      } else if (errorData?.error) {

        errorMessage = errorData.error;

      } else if (error.message) {

        errorMessage = error.message;

      }



      if (errorData?.code === 'account_verification_expired') {

        clearStoredAuth();

      }



      return rejectWithValue(errorMessage);

    }

  }

);



export const getCurrentUser = createAsyncThunk(

  'auth/getCurrentUser',

  async (_, { rejectWithValue }) => {

    try {

      const response = await api.get('/user');

      const data = response.data;

      const user = mergeUserFromAuthPayload(data) || data.user || data;



      const verificationRequired =

        data.user?.verification_required ?? needsEmailVerification(user);
      const mustChangePassword =
        data.user?.must_change_password ?? needsPasswordChange(user);



      localStorage.setItem('auth_user', JSON.stringify(user));

      localStorage.setItem(

        'auth_verification_required',

        verificationRequired ? 'true' : 'false'

      );
      localStorage.setItem(
        'auth_must_change_password',
        mustChangePassword ? 'true' : 'false'
      );



      const subscription = extractSubscriptionFromUserPayload(data);

      return { user, verificationRequired, mustChangePassword, subscription };

    } catch (error) {

      const errorData = error.response?.data;

      const code = errorData?.code;



      if (error.response?.status === 403 && code === 'email_verification_required') {

        localStorage.setItem('auth_verification_required', 'true');

        return rejectWithValue({

          type: 'verification_required',

          message: errorData.message || 'Please verify your email address.',

        });

      }



      if (error.response?.status === 403 && code === 'account_verification_expired') {

        clearStoredAuth();

        return rejectWithValue({

          type: 'account_verification_expired',

          message:

            errorData.message ||

            'Your account has expired. Please sign up again.',

        });

      }



      clearStoredAuth();

      return rejectWithValue(

        errorData?.message || 'Failed to get user'

      );

    }

  }

);



const applyOrganizationAuthPayload = (currentUser, data) => {
  const user = mergeUserWithOrganizations(
    { ...currentUser, ...(data.user || {}) },
    data.organizations ?? currentUser?.organizations ?? []
  );

  localStorage.setItem('auth_user', JSON.stringify(user));

  if (user.primary_organization_id != null || user.active_organization_id != null || user.organization_id != null) {
    setActiveOrganizationId(
      user.active_organization_id ?? user.organization_id ?? user.primary_organization_id
    );
  }

  const subscription = extractSubscriptionFromUserPayload(data);

  return { user, subscription };
};

export const createAdditionalOrganization = createAsyncThunk(
  'auth/createAdditionalOrganization',
  async (payload, { dispatch, getState, rejectWithValue }) => {
    try {
      const data = await createAdditionalOrganizationRequest(payload);
      const currentUser = getState().auth.user;

      if (data.organizations || data.user) {
        return applyOrganizationAuthPayload(currentUser, data);
      }

      const refreshed = await dispatch(getCurrentUser()).unwrap();
      return {
        user: refreshed.user,
        subscription: refreshed.subscription,
      };
    } catch (error) {
      const errorData = error.response?.data;
      const message = errorData?.message || error.message || 'Failed to create organization';
      if (errorData?.errors) {
        return rejectWithValue({ message, errors: errorData.errors });
      }
      return rejectWithValue(message);
    }
  }
);

export const switchOrganizationPrimary = createAsyncThunk(
  'auth/switchOrganizationPrimary',
  async (organizationId, { dispatch, rejectWithValue }) => {
    try {
      await switchOrganizationPrimaryRequest(organizationId);
      return await dispatch(getCurrentUser()).unwrap();
    } catch (error) {
      const errorData = error.response?.data;
      return rejectWithValue(
        errorData?.message || error.message || 'Failed to switch organization'
      );
    }
  }
);

export const logout = createAsyncThunk(

  'auth/logout',

  async (_, { rejectWithValue }) => {

    try {
      // Unregister FCM web token before clearing auth (needs Bearer).
      try {
        const { unregisterWebPush } = await import('../../utils/webPush');
        await unregisterWebPush();
      } catch {
        // Push cleanup is best-effort
      }

      const response = await api.post('/logout');

      clearStoredAuth();

      clearCsrfToken();

      return response.data?.message || 'Logout successful';

    } catch (error) {

      try {
        const { unregisterWebPush } = await import('../../utils/webPush');
        await unregisterWebPush();
      } catch {
        // ignore
      }

      clearStoredAuth();

      clearCsrfToken();

      return rejectWithValue(error.response?.data?.message || 'Logout failed');

    }

  }

);



export const register = createAsyncThunk(

  'auth/register',

  async (data, { rejectWithValue }) => {

    try {

      const payload = {

        name: data.name,

        email: data.email,

        password: data.password,

        password_confirmation: data.password_confirmation,

        phone: data.phone || '',

        company_name: data.company_name || '',

        company_size: data.company_size || '',

        address_street: data.address_street || '',

        address_city: data.address_city || '',

        address_state: data.address_state || '',

        address_postal_code: data.address_postal_code || '',

        address_country: data.address_country || '',

        user_tier: data.user_tier || 'business',

      };



      const response = await api.post('/signup', payload);

      const auth = normalizeAuthResponse(response.data, { fallbackEmail: data.email });

      if (!auth.token) {

        return rejectWithValue('Invalid response from server. Token not found.');

      }

      if (auth.user?.email || data.email) {
        localStorage.setItem('auth_pending_email', auth.user?.email || data.email);
      }

      persistAuth({

        token: auth.token,

        user: auth.user,

        verificationRequired: true,
        mustChangePassword: false,

      });



      return {

        ...auth,

        verificationRequired: true,

        status: response.data?.status || 'pending_verification',

        message: auth.message || response.data?.message,

      };

    } catch (error) {

      const errorData = error.response?.data;



      if (errorData?.errors) {

        return rejectWithValue({

          message: errorData.message || 'Validation failed',

          errors: errorData.errors,

        });

      }



      return rejectWithValue({

        message: errorData?.message || 'Registration failed. Please try again.',

        errors: null,

      });

    }

  }

);



export const verifyEmail = createAsyncThunk(

  'auth/verifyEmail',

  async (otpCode, { rejectWithValue }) => {

    try {

      const data = await verifySignupEmail(otpCode);

      const auth = normalizeAuthResponse(data);



      if (auth.token) {

        localStorage.setItem('auth_token', auth.token);

      }

      if (auth.user) {

        localStorage.setItem('auth_user', JSON.stringify(auth.user));

      }

      localStorage.setItem('auth_verification_required', 'false');
      localStorage.removeItem('auth_pending_email');

      return enrichAuthFromProfile(auth);

    } catch (error) {

      const errorData = error.response?.data;

      if (errorData?.code === 'account_verification_expired') {

        clearStoredAuth();

        return rejectWithValue({

          type: 'account_verification_expired',

          message:

            errorData.message ||

            'Your account has expired. Please sign up again.',

        });

      }



      if (errorData?.errors?.otp_code) {

        return rejectWithValue({

          message: errorData.errors.otp_code[0],

          errors: errorData.errors,

        });

      }



      return rejectWithValue({

        message: errorData?.message || 'Verification failed. Please try again.',

        errors: errorData?.errors || null,

      });

    }

  }

);



export const resendVerificationEmail = createAsyncThunk(

  'auth/resendVerificationEmail',

  async (_, { rejectWithValue }) => {

    try {

      const data = await resendSignupVerification();

      return {

        message: data.message || 'A new verification code has been sent to your email.',

        otpExpiresInMinutes: data.otp_expires_in_minutes ?? null,

      };

    } catch (error) {

      const errorData = error.response?.data;



      if (errorData?.code === 'account_verification_expired') {

        clearStoredAuth();

        return rejectWithValue({

          type: 'account_verification_expired',

          message:

            errorData.message ||

            'Your account has expired. Please sign up again.',

        });

      }



      return rejectWithValue(

        errorData?.message || 'Unable to send verification code. Please try again.'

      );

    }

  }

);



export const completeInvitedPasswordSetup = createAsyncThunk(

  'auth/completeInvitedPasswordSetup',

  async (payload, { rejectWithValue }) => {

    try {

      const data = await completeInvitedPasswordSetupRequest(payload);
      const auth = normalizeAuthResponse(data);

      if (auth.user) {
        localStorage.setItem('auth_user', JSON.stringify(auth.user));
      }
      localStorage.setItem('auth_must_change_password', 'false');
      localStorage.setItem('auth_verification_required', 'false');

      return enrichAuthFromProfile(auth);

    } catch (error) {

      return rejectWithValue(
        error.response?.data?.message || 'Unable to complete password setup.'
      );

    }

  }

);


export const authSlice = createSlice({

  name: 'auth',

  initialState,

  reducers: {

    setUser: (state, action) => {

      state.user = action.payload;

      state.verificationRequired = needsEmailVerification(action.payload);
      state.mustChangePassword = needsPasswordChange(action.payload);

      if (action.payload) {

        localStorage.setItem('auth_user', JSON.stringify(action.payload));

        localStorage.setItem(

          'auth_verification_required',

          state.verificationRequired ? 'true' : 'false'

        );
        localStorage.setItem(
          'auth_must_change_password',
          state.mustChangePassword ? 'true' : 'false'
        );

      }

    },

    clearUser: (state) => {

      state.user = null;

      state.token = null;

      state.verificationRequired = false;
      state.mustChangePassword = false;

      state.status = 'idle';

      state.error = null;

      clearStoredAuth();

    },

    setToken: (state, action) => {

      state.token = action.payload;

      if (action.payload) {

        localStorage.setItem('auth_token', action.payload);

      }

    },

    setAuthFromToken: (state, action) => {
      const payload = action.payload || {};
      const token = typeof payload === 'string' ? payload : payload.token;
      const user = typeof payload === 'string' ? null : payload.user || null;
      const verificationRequired =
        typeof payload === 'string'
          ? false
          : payload.verificationRequired !== undefined
            ? !!payload.verificationRequired
            : needsEmailVerification(user);
      const mustChangePassword =
        typeof payload === 'string'
          ? false
          : payload.mustChangePassword !== undefined
            ? !!payload.mustChangePassword
            : needsPasswordChange(user);

      state.token = token || null;
      state.user = user;
      state.verificationRequired = verificationRequired;
      state.mustChangePassword = mustChangePassword;
      state.status = token ? 'succeeded' : 'idle';
      state.error = null;

      persistAuth({
        token,
        user,
        verificationRequired,
        mustChangePassword,
      });
    },

    clearError: (state) => {

      state.error = null;

    },

  },

  extraReducers: (builder) => {

    builder

      .addCase(login.pending, (state) => {

        state.status = 'loading';

        state.error = null;

      })

      .addCase(login.fulfilled, (state, action) => {

        state.user = action.payload.user || null;

        state.token = action.payload.token || null;

        state.verificationRequired = action.payload.verificationRequired;
        state.mustChangePassword = action.payload.mustChangePassword === true;

        state.status = 'succeeded';

        state.error = null;

      })

      .addCase(login.rejected, (state, action) => {

        state.status = 'failed';

        state.user = null;

        state.token = null;

        state.verificationRequired = false;
        state.mustChangePassword = false;

        state.error = action.payload;

      })

      .addCase(register.pending, (state) => {

        state.status = 'loading';

        state.error = null;

      })

      .addCase(register.fulfilled, (state, action) => {

        state.user = action.payload.user || null;

        state.token = action.payload.token || null;

        state.verificationRequired = true;
        state.mustChangePassword = false;

        state.status = 'succeeded';

        state.error = null;

      })

      .addCase(register.rejected, (state, action) => {

        state.status = 'failed';

        state.error = action.payload;

      })

      .addCase(getCurrentUser.pending, (state) => {

        state.status = 'loading';

      })

      .addCase(getCurrentUser.fulfilled, (state, action) => {

        state.user = action.payload.user;

        state.verificationRequired = action.payload.verificationRequired;
        state.mustChangePassword = action.payload.mustChangePassword === true;

        state.status = 'succeeded';

        state.error = null;

      })

      .addCase(switchOrganizationPrimary.fulfilled, (state, action) => {

        state.user = action.payload.user;

      })

      .addCase(createAdditionalOrganization.fulfilled, (state, action) => {

        state.user = action.payload.user;

      })

      .addCase(getCurrentUser.rejected, (state, action) => {

        if (action.payload?.type === 'verification_required') {

          state.verificationRequired = true;
          state.mustChangePassword = false;

          state.status = 'succeeded';

          state.error = null;

          return;

        }



        state.user = null;

        state.token = null;

        state.verificationRequired = false;
        state.mustChangePassword = false;

        state.status = 'idle';

        state.error = action.payload;

      })

      .addCase(verifyEmail.pending, (state) => {

        state.status = 'loading';

        state.error = null;

      })

      .addCase(verifyEmail.fulfilled, (state, action) => {

        state.user = action.payload.user || state.user;

        state.token = action.payload.token || state.token;

        state.verificationRequired = false;
        state.mustChangePassword = false;

        state.status = 'succeeded';

        state.error = null;

      })

      .addCase(verifyEmail.rejected, (state, action) => {

        state.status = 'succeeded';

        state.error = action.payload;

        if (action.payload?.type === 'account_verification_expired') {

          state.user = null;

          state.token = null;

          state.verificationRequired = false;
          state.mustChangePassword = false;

        }

      })

      .addCase(resendVerificationEmail.pending, (state) => {

        state.error = null;

      })

      .addCase(resendVerificationEmail.rejected, (state, action) => {

        state.error = action.payload;

        if (action.payload?.type === 'account_verification_expired') {

          state.user = null;

          state.token = null;

          state.verificationRequired = false;
          state.mustChangePassword = false;

        }

      })

      .addCase(completeInvitedPasswordSetup.pending, (state) => {

        state.status = 'loading';
        state.error = null;

      })
      .addCase(completeInvitedPasswordSetup.fulfilled, (state, action) => {

        state.user = action.payload.user || state.user;
        state.token = action.payload.token || state.token;
        state.verificationRequired = false;
        state.mustChangePassword = false;
        state.status = 'succeeded';
        state.error = null;

      })
      .addCase(completeInvitedPasswordSetup.rejected, (state, action) => {

        state.status = 'failed';
        state.error = action.payload;

      })
      .addCase(logout.pending, (state) => {

        state.status = 'loading';

      })

      .addCase(logout.fulfilled, (state) => {

        state.user = null;

        state.token = null;

        state.verificationRequired = false;
        state.mustChangePassword = false;

        state.status = 'idle';

        state.error = null;

      })

      .addCase(logout.rejected, (state, action) => {

        state.user = null;

        state.token = null;

        state.verificationRequired = false;
        state.mustChangePassword = false;

        state.status = 'idle';

        state.error = action.payload;

      });

  },

});



export const { setUser, clearUser, setToken, setAuthFromToken, clearError } = authSlice.actions;

export default authSlice.reducer;

